import { useEffect, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import type { IncentivesResponse } from 'src/payments/hooks/use-incentives/use-incentives';

type IncentiveExposureParams = Pick<IncentivesResponse, 'incentiveValue' | 'isCashBack' | 'userType'> & {
  // Which surface the exposure fired on (e.g. checkout_payment_selection, order_confirmation,
  // product_description).
  source: string;
};

// Emit "incentive exposure" once each time an incentive treatment becomes visible (Q5: do incentives drive
// enrollment / first payment), gated behind the rollout flag for a gradual rollout (serviceFeeShown precedent).
// Token-gated so the join key isn't empty before useDutchiePayAnalytics resolves; the ref resets when the
// incentive disappears so re-showing the treatment re-fires. Shared by every incentive banner/accordion surface.
export const useIncentiveExposure = ({
  incentiveValue,
  userType,
  isCashBack,
  source,
}: IncentiveExposureParams): void => {
  const flags = useFlags();
  const { trackIncentiveExposure, hasSessionToken } = useDutchiePayAnalytics();
  const incentiveExposureTelemetryEnabled = flags['fintech.platform.incentive-exposure-telemetry.rollout'];
  const exposureTrackedRef = useRef(false);

  useEffect(() => {
    if (!incentiveValue) {
      exposureTrackedRef.current = false;
      return;
    }
    if (exposureTrackedRef.current || !hasSessionToken || !incentiveExposureTelemetryEnabled) {
      return;
    }
    exposureTrackedRef.current = true;
    trackIncentiveExposure({ userType, incentiveValue: String(incentiveValue), isCashBack, source });
  }, [
    incentiveValue,
    userType,
    isCashBack,
    source,
    hasSessionToken,
    incentiveExposureTelemetryEnabled,
    trackIncentiveExposure,
  ]);
};
